/* eslint-disable */
import { Autocomplete, Box, TextField, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import { createElement, FC, useEffect, useState } from 'react'
import { MenuItemLink, MenuProps, useGetList, usePermissions, useResourceDefinitions, useSidebarState, useTranslate } from 'react-admin'
import { Dot } from '../components/Icons'
import useAutoLogout from '../hooks/useAutoLogout'
import useSwitchOrganization from '../hooks/useSwitchOrganization'
import SubMenu from './SubMenu'
import { border, color, xxllncColor } from './themes'
import { CERTIFICATE_ORGANIZATION_SWITCH_SCOPE } from '../config'

const Menu: FC<MenuProps> = ({ dense = false }) => {
  const translate = useTranslate()
  const switchOrganization = useSwitchOrganization()
  const { permissions } = usePermissions()
  const resourcesDefinitions = useResourceDefinitions()
  const resources = Object.keys(resourcesDefinitions).map(name => resourcesDefinitions[name])
  const [open, setOpen] = useSidebarState()
  const [state, setState] = useState({ menuSettings: false })
  const [selectedOrg, setSelectedOrg] = useState(localStorage.getItem('org_id_override') || '')
  const [selectedOrgName, setSelectedOrgName] = useState(localStorage.getItem('org_name_override') || '')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const canSwitchOrg = !!permissions?.scopes?.includes(CERTIFICATE_ORGANIZATION_SWITCH_SCOPE)

  // Keep the selection in sync when it is changed elsewhere (e.g. after creating an organization).
  useEffect(() => {
    const sync = () => {
      setSelectedOrg(localStorage.getItem('org_id_override') || '')
      setSelectedOrgName(localStorage.getItem('org_name_override') || '')
    }
    window.addEventListener('orgChange', sync)
    return () => window.removeEventListener('orgChange', sync)
  }, [])

  // Debounce so not every keystroke hits the API.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  // Server-side search: the list can hold more organizations than one page.
  const { data: organizationsData, isFetching } = useGetList(
    'organization',
    { pagination: { page: 1, perPage: 50 }, sort: { field: 'name', order: 'ASC' }, filter: { q: debouncedSearch } },
    { enabled: canSwitchOrg, staleTime: 60_000 }
  )

  // The selected org may not be in the search results: look up its name by id so the display is never empty.
  const { data: selectedOrgData } = useGetList(
    'organization',
    { pagination: { page: 1, perPage: 5 }, sort: { field: 'name', order: 'ASC' }, filter: { q: selectedOrg } },
    { enabled: canSwitchOrg && !!selectedOrg && !selectedOrgName, staleTime: 60_000 }
  )
  const resolvedName = selectedOrgName ||
    selectedOrgData?.find(org => org.organizationId === selectedOrg)?.name ||
    selectedOrg
  const selectedOption = selectedOrg ? { organizationId: selectedOrg, name: resolvedName } : null
  const organizations = canSwitchOrg ? organizationsData ?? [] : []

  useAutoLogout()

  const handleOrgChange = (newOrgId: string, newOrgName: string) => {
    setSelectedOrg(newOrgId)
    setSelectedOrgName(newOrgName)
    switchOrganization(newOrgId, newOrgName)
  }

  const handleToggle = (menu: MenuName) => {
    setState(stateItem => ({ ...stateItem, [menu]: !stateItem[menu] }))
  }

  const getMenuItems = (object) => {
    let values = object.filter(resource => resource.icon && !resource.options?.submenu)
    const submenu = object.filter(resource => resource.icon && resource.options?.submenu)

    if (submenu.length > 0) {
      values = [
        ...values,
        {
          name: 'submenu.settings',
          resources: submenu.map(prop => prop.name),
          submenu: [...submenu]
        }
      ]
    }

    return values
  }

  return (
    <StyledBox mt={1} className={classes.root}>
      <div className={classes.spacer}>&nbsp;</div>
      {getMenuItems(resources).map(resource => {
        if ('submenu' in resource) {
          return (
            <SubMenu
              key={resource.name}
              handleToggle={() => handleToggle('menuSettings')}
              isOpen={state.menuSettings}
              sidebarIsOpen={!open}
              name={resource.name}
              dense={dense}
            >
              {resource.submenu.map(item => (
                <StyledMenuItemLink
                  className={classes.link}
                  classes={classes}
                  dense={dense}
                  key={item.name}
                  leftIcon={<Dot fill='#fff' />}
                  primaryText={item.options?.label || item.name}
                  sidebarIsOpen={!open}
                  to={`/${item.name}`}
                />
              ))}
            </SubMenu>
          )
        }
        return (
          <StyledMenuItemLink
            className={classes.item}
            dense={dense}
            key={resource.name}
            primaryText={resource.options?.label || resource.name}
            sidebarIsOpen={!open}
            to={`/${resource.name}`}
            leftIcon={createElement(resource.icon)}
          />
        )
      })}
      <div className={classes.footer}>
      {permissions?.scopes?.includes(CERTIFICATE_ORGANIZATION_SWITCH_SCOPE) && (
        <Autocomplete
          className={classes.select}
          options={selectedOption && !organizations.some(org => org.organizationId === selectedOrg)
            ? [selectedOption, ...organizations]
            : organizations}
          value={selectedOption}
          loading={isFetching}
          filterOptions={options => options}
          getOptionLabel={org => org.name}
          isOptionEqualToValue={(org, value) => org.organizationId === value.organizationId}
          onInputChange={(_event, value, reason) => {
            if (reason === 'input') setSearch(value)
            if (reason === 'reset' || reason === 'clear') setSearch('')
          }}
          onChange={(_event, org) => org && handleOrgChange(org.organizationId, org.name)}
          renderOption={(props, org) => <li {...props} key={org.organizationId}>{org.name}</li>}
          renderInput={params => <TextField {...params} placeholder={translate('data.selectOrganization')} />}
        />
      )}
      </div>
    </StyledBox>
  )
}

type MenuName = 'menuSettings'

const PREFIX = 'xxllncMenu'

const classes = {
  root: `${PREFIX}-root`,
  link: `${PREFIX}-link`,
  branding: `${PREFIX}-branding`,
  logo: `${PREFIX}-logo`,
  lab: `${PREFIX}-lab`,
  active: `${PREFIX}-active`,
  icon: `${PREFIX}-icon`,
  item: `${PREFIX}-item`,
  spacer: `${PREFIX}-spacer`,
  select: `${PREFIX}-select`,
  footer: `${PREFIX}-footer`,
}

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    overflow: 'hidden',
  },
  [`& .${classes.select}`]: {
    margin: '10px',
    width: '90%',
    color: xxllncColor.dark100,
  },
  [`& .${classes.link}`]: {
    [theme.breakpoints.up('sm')]: {
      color: xxllncColor.light100,
      ['&:hover']: {
        backgroundColor: xxllncColor.dark100,
      },
    }
  },
  [`& .${classes.item}`]: {
    padding: '12px 13px',
    ['&:hover']: {
      [theme.breakpoints.up('sm')]: {
        backgroundColor: xxllncColor.light100,
        color: xxllncColor.dark100,
      }
    }
  },
  [`& .${classes.branding}`]: {
    display: 'flex',
    flexDirection: 'row',
  },
  [`& .${classes.logo}`]: {
    fill: color.common.inactive,
    width: theme.spacing(10),
    marginTop: theme.spacing(1.3),
    marginLeft: theme.spacing(2.5),
    marginRight: theme.spacing(1.5),
  },
  [`& .${classes.lab}`]: {
    backgroundColor: xxllncColor.opDark80,
    color: xxllncColor.opLight60,
    borderRadius: border.radius.lg,
    fontSize: 9,
    fontWeight: 700,
    marginTop: '14px',
    padding: '5px 8px',
  },
  [`& .${classes.spacer}`]: {
    height: '80px',
  },  
  [`& .${classes.footer}`]: {
    textAlign: 'center',
    color: '#fff',
    position: 'absolute',
    bottom: 10,
    width: '100%',
    left: 0,
    '& ul': {
      listStyleType: 'none',
      padding: 0,
      margin: 0,
    },
    '& li': {
      color: '#fff',
      fontSize: '0.9rem',
    },
    '& a': {
      color: '#fff',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
}))

const StyledMenuItemLink = styled(MenuItemLink)(({ theme }) => ({
  '&.RaMenuItemLink-active': {
    backgroundColor: xxllncColor.light100,
    color: xxllncColor.dark100,
  },
}))

export default Menu